import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ShieldCheck, ShieldX, Search, Loader2, Smartphone, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyWarrantyPage() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/warranty-verify`, { params: { q: query.trim() } });
      setResult(data);
    } catch {
      setResult({ found: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#005DE8] rounded-lg flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-lg text-slate-900" style={{ fontFamily: 'Outfit' }}>Mobikoo</span>
          </div>
          <Link to="/login">
            <Button variant="ghost" size="sm" className="text-slate-600">
              <ArrowLeft className="h-4 w-4 mr-1" /> Partner Login
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-[#005DE8] rounded-2xl flex items-center justify-center mx-auto mb-5">
            <ShieldCheck className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>
            Verify Your Warranty
          </h1>
          <p className="text-slate-600 text-lg max-w-md mx-auto">
            Enter your warranty certificate number or device IMEI to check warranty status
          </p>
        </div>

        {/* Search */}
        <Card className="border-slate-200 shadow-lg mb-8">
          <CardContent className="pt-6 pb-6">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Certificate # (e.g., WC-...) or IMEI (15 digits)"
                  className="pl-12 h-14 text-base rounded-xl border-slate-300 focus:border-[#005DE8]"
                  data-testid="verify-search-input"
                />
              </div>
              <Button type="submit" className="bg-[#005DE8] hover:bg-[#0047B3] h-14 px-8 rounded-xl text-base" disabled={loading} data-testid="verify-search-btn">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && !loading && result && !result.found && (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <ShieldX className="h-14 w-14 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Warranty Found</h3>
              <p className="text-slate-500">
                No warranty record found for "<span className="font-mono">{query}</span>". Please double-check the certificate number or IMEI.
              </p>
            </CardContent>
          </Card>
        )}

        {result?.found && result.warranty && (
          <div className="space-y-5" data-testid="verify-result">
            {/* Warranty Status Card */}
            <Card className={`border-2 overflow-hidden ${result.warranty.is_active ? 'border-emerald-300' : 'border-red-300'}`}>
              <div className={`px-6 py-4 ${result.warranty.is_active ? 'bg-emerald-500' : 'bg-red-500'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-white">
                    {result.warranty.is_active ? <CheckCircle className="h-6 w-6" /> : <XCircle className="h-6 w-6" />}
                    <span className="text-lg font-semibold">
                      Warranty {result.warranty.status}
                    </span>
                  </div>
                  <Badge className="bg-white/20 text-white border-white/30 border text-sm px-3">
                    {result.warranty.plan_name}
                  </Badge>
                </div>
              </div>
              <CardContent className="pt-5">
                <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
                  <div>
                    <span className="text-slate-500 block mb-0.5">Certificate Number</span>
                    <span className="font-mono font-semibold text-slate-900 text-base">{result.warranty.certificate_number}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block mb-0.5">Duration</span>
                    <span className="font-medium text-slate-900">{result.warranty.duration_months} months</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block mb-0.5">Valid From</span>
                    <span className="font-medium text-slate-900">{formatDate(result.warranty.start_date)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block mb-0.5">Valid Until</span>
                    <span className="font-medium text-slate-900">{formatDate(result.warranty.end_date)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Device & Customer Info */}
            <Card className="border-slate-200">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                    <Smartphone className="h-5 w-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{result.device?.brand} {result.device?.model}</p>
                    <p className="text-sm text-slate-500 mt-0.5">Registered to: {result.customer_name}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {result?.found && !result.warranty && (
          <Card className="border-slate-200">
            <CardContent className="py-10 text-center">
              <ShieldX className="h-12 w-12 text-amber-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Device Found — No Warranty</h3>
              <p className="text-slate-500">{result.message || 'This device was sold without an extended warranty.'}</p>
              <div className="mt-4 inline-flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg text-sm">
                <Smartphone className="h-4 w-4 text-slate-400" />
                <span className="text-slate-700">{result.device?.brand} {result.device?.model}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer info */}
        {!searched && (
          <div className="text-center text-sm text-slate-400 mt-8">
            <p>Your warranty certificate number can be found on your purchase receipt or the warranty certificate provided at the time of purchase.</p>
          </div>
        )}
      </main>
    </div>
  );
}
